import React, { useState } from 'react';
import PropTypes, { element } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import routeConfiguration from '../../routeConfiguration';
import jsPDF from 'jspdf';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ReactComponent as InfoIcon } from '../../assets/svg/infoIcon.svg';

import html2canvas from 'html2canvas';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  propTypes,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureOwnListing } from '../../util/data';
import {
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import config from '../../config';
import {
  InlineTextButton,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  IconSpinner,
  ResponsiveImage,
  Modal,
  Form,
  Logo,
  Button,
  PrimaryButton,
  CreateGIgDisabledInfo,
} from '../../components';

import MenuIcon from './MenuIcon';
import Overlay from './Overlay';
import css from './ManageListingCard.module.css';
import { findOptionsByFilterLabel, findOptionsForSelectFilter } from '../../util/search';
import { ReactComponent as Location } from '../../assets/locationPinNew.svg';
import moment from 'moment';
import { ReactComponent as CloseIcon } from '../../assets/svg/close.svg';
import { ReactComponent as EditIcon } from '../../assets/svg/editPen.svg';
import { ReactComponent as DotIcon } from '../../assets/svg/threedots.svg';
import { ReactComponent as CopyIcon } from '../../assets/svg/copyIcon.svg';
import { ReactComponent as Download } from '../../assets/svg/downloadIcon.svg';
import { types as sdkTypes } from '../../util/sdkLoader';
import Invoice from '../Invoice/Invoice';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api';
import ReadMoreButton from '../ReadMoreButton/ReadMoreButton';
import { languageSelectOptions } from '../../marketplace-custom-config';
// Menu content needs the same padding
const MENU_CONTENT_OFFSET = -12;
const MAX_LENGTH_FOR_WORDS_IN_TITLE = 7;
const { Money } = sdkTypes;
const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ManageListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const createListingURL = (routes, listing) => {
  const id = listing.id.uuid;
  const slug = createSlug(listing.attributes.title);
  const isPendingApproval = listing.attributes.state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = listing.attributes.state === LISTING_STATE_DRAFT;
  const variant = isDraft
    ? LISTING_PAGE_DRAFT_VARIANT
    : isPendingApproval
    ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
    : null;

  const linkProps =
    isPendingApproval || isDraft
      ? {
          name: 'ListingPageVariant',
          params: {
            id,
            slug,
            variant,
          },
        }
      : {
          name: 'ListingPage',
          params: { id, slug },
        };

  return createResourceLocatorString(linkProps.name, routes, linkProps.params, {});
};

// Cards are not fixed sizes - So, long words in title make flexboxed items to grow too big.
// 1. We split title to an array of words and spaces.
//    "foo bar".split(/([^\s]+)/gi) => ["", "foo", " ", "bar", ""]
// 2. Then we break long words by adding a '<span>' with word-break: 'break-all';
const formatTitle = (title, maxLength) => {
  const nonWhiteSpaceSequence = /([^\s]+)/gi;
  return title.split(nonWhiteSpaceSequence).map((word, index) => {
    return word.length > maxLength ? (
      <span key={index} style={{ wordBreak: 'break-all' }}>
        {word}
      </span>
    ) : (
      word
    );
  });
};

export const ManageListingCardComponent = props => {
  const {
    className,
    rootClassName,
    hasClosingError,
    hasOpeningError,
    history,
    intl,
    params,
    search,
    actionsInProgressListingId,
    listing,
    onCloseListing,
    onOpenListing,
    onToggleMenu,
    renderSizes,
    availabilityEnabled,
    userNativeLang,
    user,
    onManageDisableScrolling,
    key,
    getInvoiceData,
    invoiceData,
    onCopyOwnListing,
  } = props;
  const [dotIconDiv, setDotIconDiv] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const assignedWorkers = currentListing?.attributes?.privateData?.assigendWorkers || [];
  const appliedWorkers = currentListing?.attributes?.publicData?.appliedCust || [];
  const hasWorkers = assignedWorkers.length > 0 || appliedWorkers.length > 0;

  const id = currentListing.id.uuid;
  const { title = '', price, state } = currentListing.attributes;
  const slug = createSlug(title);
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const shouldShowInvoice =
    currentListing?.attributes?.metadata?.acceptedWorkers?.length > 0 &&
    !currentListing?.attributes?.metadata?.hasAnyDispute &&
    (!currentListing?.attributes?.metadata?.isCancelledByBusiness ||
      currentListing?.attributes?.metadata?.penaltyAdded) &&
    currentListing?.attributes?.metadata?.isWorkerPaid;

  const menuItemClasses = classNames(css.menuItem, {
    [css.menuItemDisabled]: !!actionsInProgressListingId,
  });

  const isCancelledByBusiness = currentListing?.attributes?.metadata?.isCancelledByBusiness;
  const cancelDate = isCancelledByBusiness ? currentListing?.attributes?.metadata?.closeDate : null;
  const sep = userNativeLang === languageSelectOptions.fr.value ? 'à' : 'at';
  const cancelDateStr = cancelDate
    ? `${moment(cancelDate).format('DD MMM YYYY')} ${sep} ${moment(cancelDate).format('HH[h]mm')}`
    : '';

  const { formattedPrice, priceTitle } = priceData(price || new Money(0, config.currency), intl);

  const hasError = hasOpeningError || hasClosingError;
  const thisListingInProgress =
    actionsInProgressListingId && actionsInProgressListingId.uuid === id;

  const onOverListingLink = () => {
    // Enforce preloading of ListingPage (loadable component)
    const { component: Page } = findRouteByRouteName('ListingPage', routeConfiguration());
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };
  const handleOpenInvoiceModal = ({ e, id }) => {
    e.preventDefault();

    setShowModal(true);
    getInvoiceData(id);
  };
  const generateInvoice = ({ e, id, name, status }) => {
    setDownloading(true);
    e.preventDefault();

    //working but not npm supported

    // const element = document.getElementById(id);

    // const options = {
    //   filename: 'Listing-invoice.pdf', // Set the desired file name here
    //   margin: 10,
    //   jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    // };
    // html2pdf(element, options);

    //-----------------------------------------------------------
    const element = document.getElementById(id);
    html2canvas(element, { scale: 8 }).then(canvas => {
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const doc = new jsPDF('p', 'mm', 'a4');
      const compWidth = doc.internal.pageSize.getWidth();
      const compHeight = doc.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Define the desired upper and lower gap size
      // const upperGap = 10; // Adjust as needed
      // const lowerGap = 10; // Adjust as needed
      //   const availableHeight = compHeight - upperGap - lowerGap;
      //   const ratio = availableHeight / imgHeight;
      //   const newImgHeight = imgHeight * ratio;

      //   // Calculate the vertical position to reduce the upper gap
      //   const imgY = upperGap;

      //   const imgX = (compWidth - imgWidth * ratio) / 2;
      const ratio = Math.min(compWidth / imgWidth, compHeight / imgHeight);
      const imgX = (compWidth - imgWidth * ratio) / 2;
      const imgY = 20;
      // const imgY = (compHeight - imgHeight * ratio) / 2;

      doc.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      // doc.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, newImgHeight);
      doc.setProperties({
        image_compression: 'NONE',
      });
      {
        status === 'succeeded'
          ? doc.save(`${name}_Original Invoice.pdf`)
          : doc.save(`${name}_Draft Invoice.pdf`);
      }
      setDownloading(false);
    });
  };

  const seeListing = event => {
    event.preventDefault();
    event.stopPropagation();
    // ManageListingCard contains links, buttons and elements that are working with routing.
    // This card doesn't work if <a> or <button> is used to wrap events that are card 'clicks'.
    //
    // NOTE: It might be better to absolute-position those buttons over a card-links.
    // (So, that they have no parent-child relationship - like '<a>bla<a>blaa</a></a>')
    history.push(createListingURL(routeConfiguration(), listing));
  };

  const formatDate = date => {
    return userNativeLang === languageSelectOptions.fr.value
      ? moment(date).format('Do MMM y')
      : moment(date).format('Do MMM, y');
  };

  const titleClasses = classNames(css.title, {
    [css.titlePending]: isPendingApproval,
    [css.titleDraft]: isDraft,
  });

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const unitType = config.bookingUnitType;
  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey = isNightly
    ? 'ManageListingCard.perNight'
    : isDaily
    ? 'ManageListingCard.perDay'
    : 'ManageListingCard.perUnit';
  const filterConfig = config.custom.filters;
  const jobCategoryOptions = findOptionsForSelectFilter('category', filterConfig);
  const jobSubCategoryOptions = findOptionsByFilterLabel('Subcategory', filterConfig);
  const reducedJobSubCategory = Object.values(jobSubCategoryOptions).reduce(
    (sum, curr) => [...sum, ...curr],
    []
  );
  const scheduleType = currentListing?.attributes?.publicData?.scheduleType;
  const publishedDate = currentListing?.attributes?.publicData?.gigPublishedDate;
  const gigStartDate = currentListing?.attributes?.publicData?.gigStartDate;
  const gigEndDate = currentListing?.attributes?.publicData?.gigEndDate;
  const isDateClosable = gigStartDate && gigEndDate ? moment().isBefore(gigStartDate) : true;

  const oneTimeStartTimeHour =
    scheduleType === 'oneTime' &&
    currentListing?.attributes?.publicData?.oneTimeScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.oneTimeScheduleList?.map(
        (m, indx) => m?.startTime?.hours?.value
      )
    );
  const oneTimeStartTimeMin =
    scheduleType === 'oneTime' &&
    currentListing?.attributes?.publicData?.oneTimeScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.oneTimeScheduleList.map(
        (m, indx) => m?.startTime?.min?.value
      )
    );
  const oneTimeEndTimeHour =
    scheduleType === 'oneTime' &&
    currentListing?.attributes?.publicData?.oneTimeScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.oneTimeScheduleList.map(
        (m, indx) => m?.endTime?.hours?.value
      )
    );
  const oneTimeEndTimeMin =
    scheduleType === 'oneTime' &&
    currentListing?.attributes?.publicData.oneTimeScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.oneTimeScheduleList.map(
        (m, indx) => m?.endTime?.min?.value
      )
    );
  const oneTimeUnpaidBreak =
    scheduleType === 'oneTime' &&
    currentListing?.attributes?.publicData?.oneTimeScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.oneTimeScheduleList?.map(
        (m, indx) => m?.unpaidBreak?.value
      )
    );

  const recStartTimeHour =
    scheduleType === 'recurring' &&
    currentListing?.attributes?.publicData?.recurringScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.recurringScheduleList?.map(
        (m, indx) => m?.startTime?.hours?.value
      )
    );
  const recStartTimeMin =
    scheduleType === 'recurring' &&
    currentListing?.attributes?.publicData?.recurringScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.recurringScheduleList?.map(
        (m, indx) => m?.startTime?.min?.value
      )
    );
  const recEndTimeHour =
    scheduleType === 'recurring' &&
    currentListing?.attributes?.publicData?.recurringScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.recurringScheduleList?.map(
        (m, indx) => m?.endTime?.hours?.value
      )
    );
  const recEndTimeMin =
    scheduleType === 'recurring' &&
    currentListing?.attributes?.publicData?.recurringScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.recurringScheduleList?.map(
        (m, indx) => m?.endTime?.min?.value
      )
    );
  const recUnpaidBreak =
    scheduleType === 'recurring' &&
    currentListing?.attributes?.publicData?.recurringScheduleList &&
    parseInt(
      currentListing?.attributes?.publicData?.recurringScheduleList?.map(
        (m, indx) => m?.unpaidBreak?.value
      )
    );

  const startTimeInMin = oneTimeStartTimeHour * 60 + oneTimeStartTimeMin;
  const endTimeInMin = oneTimeEndTimeHour * 60 + oneTimeEndTimeMin;

  const recstartTimeInMin = recStartTimeHour * 60 + recStartTimeMin;
  const recendTimeInMin = recEndTimeHour * 60 + recEndTimeMin;

  let oneTimeWorkDurationInMIn = endTimeInMin - startTimeInMin - oneTimeUnpaidBreak;
  let recWorkDurationInMIn = recendTimeInMin - recstartTimeInMin - recUnpaidBreak;

  if (oneTimeWorkDurationInMIn < 0) {
    oneTimeWorkDurationInMIn += 24 * 60;
  }
  if (recWorkDurationInMIn < 0) {
    recWorkDurationInMIn += 24 * 60;
  }
  const durationHours = Math.floor(oneTimeWorkDurationInMIn / 60);
  const durationMinutes = oneTimeWorkDurationInMIn % 60;
  const recdurationHours = Math.floor(recWorkDurationInMIn / 60);
  const recdurationMinutes = recWorkDurationInMIn % 60;
  const isProvider = user?.attributes?.profile?.publicData?.isProvider;
  const shouldClose =
    currentListing?.attributes?.state === LISTING_STATE_PUBLISHED &&
    currentListing?.attributes.state !== LISTING_STATE_CLOSED &&
    isDateClosable;
  return (
    <div className={classes}>
      <div
        className={css.threeToTwoWrapper}
        tabIndex={0}
        onMouseOver={onOverListingLink}
        onTouchStart={onOverListingLink}
      >
        {/* <div className={css.aspectWrapper}>
          <ResponsiveImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={['landscape-crop', 'landscape-crop2x']}
            sizes={renderSizes}
          />
        </div> */}
        {/* <div className={classNames(css.menuOverlayWrapper, { [css.menuOverlayOpen]: isMenuOpen })}>
          <div className={classNames(css.menuOverlay)} />
          <div className={css.menuOverlayContent}>
            <FormattedMessage id="ManageListingCard.viewListing" />
          </div>
        </div> */}
        {/* <div className={css.menubarWrapper}>
          <div className={css.menubarGradient} />
          <div className={css.menubar}>
            <Menu
              className={classNames(css.menu, { [css.cardIsOpen]: !isClosed })}
              contentPlacementOffset={MENU_CONTENT_OFFSET}
              contentPosition="left"
              useArrow={false}
              onToggleActive={isOpen => {
                const listingOpen = isOpen ? currentListing : null;
                onToggleMenu(listingOpen);
              }}
              isOpen={isMenuOpen}
            >
              <MenuLabel className={css.menuLabel} isOpenClassName={css.listingMenuIsOpen}>
                <div className={css.iconWrapper}>
                  <MenuIcon className={css.menuIcon} isActive={isMenuOpen} />
                </div>
              </MenuLabel>

              <MenuContent rootClassName={css.menuContent}>
                <MenuItem key="close-listing">
                  <InlineTextButton
                    rootClassName={menuItemClasses}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (!actionsInProgressListingId) {
                        onToggleMenu(null);
                        onCloseListing(currentListing);
                      }
                    }}
                  >
                    <FormattedMessage id="ManageListingCard.closeListing" />
                  </InlineTextButton>
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </div> */}
        {isDraft ? (
          <div className={css.draftwrapper}>
            {/* <div className={classNames({ [css.draftNoImage]: !firstImage })} /> */}
            <div className={classNames(css.listingCardwrapper)} key={currentListing?.id?.uuid}>
              <div className={css.heading}>
                <div className={css.headingName} onClick={seeListing}>
                  {' '}
                  {currentListing?.attributes?.title}
                </div>
                <span className={css.draftTag} onClick={seeListing}>
                  {intl.formatMessage({ id: 'ManageListingCard.draftGig' })}
                </span>

                {isProvider ? (
                  <div className={css.editAndCloseDiv}>
                    {hasWorkers ? null : (
                      <CreateGIgDisabledInfo
                        gigCreationDisabledChildren={
                          <InlineTextButton className={css.editDiv}>
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </InlineTextButton>
                        }
                        gigCreationEnabledChildren={
                          <NamedLink
                            className={css.editDiv}
                            name="EditListingPage"
                            params={{ id, slug, type: editListingLinkType, tab: 'description' }}
                          >
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </NamedLink>
                        }
                        popupId={'manageListingCard.edit'}
                      />
                    )}

                    {shouldClose ? (
                      <CreateGIgDisabledInfo
                        gigCreationDisabledChildren={
                          <div className={css.cancelDiv}>
                            {' '}
                            <CloseIcon className={css.CrossIcon} />
                            <FormattedMessage id="EditListingCard.closejob" />
                          </div>
                        }
                        gigCreationEnabledChildren={
                          <button
                            className={css.cancelDiv}
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              if (!actionsInProgressListingId) {
                                onToggleMenu(null);
                                if (typeof onCloseListing === 'function') {
                                  onCloseListing(currentListing);
                                }
                              }
                            }}
                          >
                            <CloseIcon className={css.CrossIcon} />
                            <FormattedMessage id="EditListingCard.closejob" />
                          </button>
                        }
                        popupId={'manageListingCard.close'}
                      />
                    ) : null}

                    {/* <button
                      className={css.draftCopyButton}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      // data-tip
                      // data-for="copyTip"
                    >
                      <div className={css.draftCopyIcon}>
                        <CopyIcon />
                        <FormattedMessage id="ManageListingCard.copyButton" />
                      </div>
                      {isDraft && (
                        <>
                          {' '}
                          <span id={`${id}copyTip`} className={css.tooltipIcon}>
                            <InfoIcon />
                          </span>
                          <ReactTooltip
                            anchorId={`${id}copyTip`}
                            place="right"
                            effect="solid"
                            clickable={false}
                            className={css.categoryTooltip}
                            content={<FormattedMessage id="ManageListingCard.copyNotAllowedTip" />}
                          />
                        </>
                      )}
                    </button> */}
                    <CreateGIgDisabledInfo
                      gigCreationDisabledChildren={
                        <div className={`${css.copyButton} ${isDraft && css.disabled}`}>
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </div>
                      }
                      gigCreationEnabledChildren={
                        <button
                          className={`${css.copyButton} ${isDraft && css.disabled}`}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (isDraft) {
                              onCopyOwnListing(listing.id.uuid).then(response => {
                                const id = response.data.id.uuid;
                                history.push(
                                  createResourceLocatorString(
                                    'EditListingPage',
                                    routeConfiguration(),
                                    { id, slug, type: 'draft', tab: 'availability' },
                                    {}
                                  )
                                );
                              });
                            }
                          }}
                        >
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </button>
                      }
                      popupId={'manageListingCard.copy'}
                    />

                    <div className={css.CardMenu}>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDotIconDiv(!dotIconDiv);
                        }}
                      >
                        <DotIcon />
                      </button>

                      <>
                        {dotIconDiv ? (
                          <div className={css.mobEditAndCloseDiv}>
                            {hasWorkers ? null : (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <InlineTextButton className={css.MobeditDiv}>
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </InlineTextButton>
                                }
                                gigCreationEnabledChildren={
                                  <NamedLink
                                    className={css.MobeditDiv}
                                    name="EditListingPage"
                                    params={{
                                      id,
                                      slug,
                                      type: editListingLinkType,
                                      tab: 'description',
                                    }}
                                  >
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </NamedLink>
                                }
                                popupId={'manageListingCard.edit'}
                              />
                            )}

                            {shouldClose ? (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <div className={css.MobcancelDiv}>
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </div>
                                }
                                gigCreationEnabledChildren={
                                  <button
                                    className={css.MobcancelDiv}
                                    onClick={event => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (!actionsInProgressListingId) {
                                        onToggleMenu(null);
                                        if (typeof onCloseListing === 'function') {
                                          onCloseListing(currentListing);
                                        }
                                      }
                                    }}
                                  >
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </button>
                                }
                                popupId={'manageListingCard.close'}
                              />
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    </div>
                  </div>
                ) : null}
              </div>
              <div onClick={seeListing}>
                <div className={css.JobDetailsSubHeadingDiv}>
                  <span className={css.category}>
                    {jobCategoryOptions
                      .filter(f =>
                        currentListing?.attributes?.publicData.jobCategories.find(
                          m => m.category === f.value
                        )
                      )
                      .map(m => m.label[userNativeLang])}
                  </span>

                  <span className={css.subcategory}>
                    -{' '}
                    {
                      reducedJobSubCategory.find(
                        m =>
                          currentListing?.attributes?.publicData.jobCategories[0].subCategory ===
                          m.value
                      ).label[userNativeLang]
                    }
                  </span>
                </div>
                {formattedPrice ? (
                  <span className={css.PriceTag}>
                    <FormattedMessage id="SearchPage.Priceperhour" /> &nbsp;
                    <span>{formattedPrice}</span>
                  </span>
                ) : null}
                <div className={css.hourDiv}>
                  {scheduleType === 'oneTime' &&
                    currentListing?.attributes?.publicData.oneTimeScheduleList &&
                    currentListing?.attributes?.publicData?.oneTimeScheduleList.map((m, indx) => {
                      return (
                        <>
                          {indx > 0 ? (
                            <>
                              {/* <hr className={css.hrColor} /> */}
                              <div className={css.workPerDay}>
                                {durationHours ? (
                                  <FormattedMessage id="SearchPage.workPerDay" />
                                ) : null}
                                &nbsp;
                                <b>
                                  {durationHours ? (
                                    <>
                                      {durationHours}
                                      {` `}
                                      <FormattedMessage id="ListingCardCustom.hours" />
                                    </>
                                  ) : null}
                                  {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                    ? ` ${durationMinutes} minutes`
                                    : null}
                                </b>{' '}
                                {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                                  <>
                                    -&nbsp;
                                    <FormattedMessage id="SearchPage.unpaidBreak" />
                                    &nbsp;
                                    <b>{m?.unpaidBreak?.value} Minutes</b>
                                  </>
                                ) : null}
                                {m?.startDate?.date ? (
                                  <>
                                    {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                    <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(m?.startDate?.date)}</b>
                                  </>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <div className={css.workPerDay}>
                              {durationHours ? (
                                <FormattedMessage id="SearchPage.workPerDay" />
                              ) : null}
                              &nbsp;
                              <b>
                                {durationHours ? (
                                  <>
                                    {durationHours}
                                    {` `}
                                    <FormattedMessage id="ListingCardCustom.hours" />
                                  </>
                                ) : null}
                                {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                  ? ` ${durationMinutes} minutes`
                                  : null}
                              </b>
                              {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                                <>
                                  &nbsp;-&nbsp;
                                  <FormattedMessage id="SearchPage.unpaidBreak" />
                                  &nbsp;
                                  <b>{m?.unpaidBreak?.value} Minutes</b>
                                </>
                              ) : null}
                              {m?.startDate?.date ? (
                                <>
                                  {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                  <FormattedMessage id="SearchPage.Days" />{' '}
                                  <b> {formatDate(m?.startDate?.date)}</b>
                                </>
                              ) : null}
                            </div>
                          )}
                          <div className={css.scheduleDiv}>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                              </span>
                              <span>
                                {m?.startTime?.hours.value} : {m?.startTime?.min.value} &nbsp;-
                                &nbsp;
                              </span>{' '}
                            </div>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                              </span>
                              <span>
                                {m?.endTime?.hours.value} : {m?.endTime?.min.value}
                              </span>{' '}
                            </div>
                          </div>
                        </>
                      );
                    })}

                  {scheduleType === 'recurring' &&
                    currentListing?.attributes?.publicData.recurringScheduleList &&
                    currentListing?.attributes?.publicData?.recurringScheduleList.map((r, indx) => {
                      const recurringDays =
                        typeof r?.recurringDays === 'string'
                          ? r?.recurringDays?.split(',')
                          : r?.recurringDays?.map(m => m);
                      return (
                        <>
                          {r?.startDate?.date !== null ? (
                            <>
                              {indx > 0 &&
                              currentListing?.attributes?.publicData?.recurringScheduleList?.[0]
                                ?.date?.date !== null ? (
                                <>
                                  {/* <hr className={css.hrColor} /> */}
                                  <div className={css.recWorkperDay}>
                                    {recdurationHours ? (
                                      <FormattedMessage id="SearchPage.workPerDay" />
                                    ) : null}
                                    &nbsp;
                                    <b>
                                      {' '}
                                      {recdurationHours ? (
                                        <>
                                          {recdurationHours}
                                          {` `}
                                          <FormattedMessage id="ListingCardCustom.hours" />
                                        </>
                                      ) : null}
                                      {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                        ? ` ${recdurationMinutes} minutes`
                                        : null}
                                    </b>{' '}
                                    {r?.unpaidBreak?.value &&
                                    parseInt(r?.unpaidBreak?.value) > 0 ? (
                                      <>
                                        -&nbsp;
                                        <FormattedMessage id="SearchPage.unpaidBreak" />
                                        &nbsp;
                                        <b>{r?.unpaidBreak?.value} Minutes</b>
                                      </>
                                    ) : null}
                                  </div>
                                  <div className={css.workPerDay}>
                                    <FormattedMessage id="SearchPage.recurringDays" />
                                    &nbsp;
                                    {recurringDays[0] === '' ? (
                                      <>
                                        <b>
                                          <FormattedMessage id="SearchPage.Daily" />
                                        </b>
                                      </>
                                    ) : (
                                      recurringDays.map((m, indx) =>
                                        indx > 0 ? (
                                          <>
                                            <b>, {m}</b>
                                          </>
                                        ) : (
                                          <b>{m}</b>
                                        )
                                      )
                                    )}
                                    &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                    <FormattedMessage id="SearchPage.to" />{' '}
                                    <b> {formatDate(r?.endDate?.date)}</b>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className={css.recWorkperDay}>
                                    {recdurationHours ? (
                                      <FormattedMessage id="SearchPage.workPerDay" />
                                    ) : null}
                                    &nbsp;
                                    <b>
                                      {' '}
                                      {recdurationHours ? (
                                        <>
                                          {recdurationHours}
                                          {` `}
                                          <FormattedMessage id="ListingCardCustom.hours" />
                                        </>
                                      ) : null}
                                      {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                        ? ` ${recdurationMinutes} minutes`
                                        : null}
                                    </b>{' '}
                                    {r?.unpaidBreak?.value &&
                                    parseInt(r?.unpaidBreak?.value) > 0 ? (
                                      <>
                                        -&nbsp;
                                        <FormattedMessage id="SearchPage.unpaidBreak" />
                                        &nbsp;
                                        <b>{r?.unpaidBreak?.value} Minutes</b>
                                      </>
                                    ) : null}
                                  </div>
                                  <div className={css.workPerDay}>
                                    <FormattedMessage id="SearchPage.recurringDays" />
                                    &nbsp;
                                    {recurringDays[0] === '' ? (
                                      <>
                                        <b>
                                          <FormattedMessage id="SearchPage.Daily" />
                                        </b>
                                      </>
                                    ) : (
                                      recurringDays.map((m, indx) =>
                                        indx > 0 ? (
                                          <>
                                            <b>, {m}</b>
                                          </>
                                        ) : (
                                          <b>{m}</b>
                                        )
                                      )
                                    )}
                                    &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                    <FormattedMessage id="SearchPage.to" />{' '}
                                    <b> {formatDate(r?.endDate?.date)}</b>
                                  </div>
                                  <div className={css.scheduleDiv}>
                                    <div className={css.workerCountDiv}>
                                      {' '}
                                      <span>
                                        <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                      </span>
                                      <span>
                                        {r?.startTime?.hours.value} : {r?.startTime?.min.value}{' '}
                                        &nbsp;- &nbsp;
                                      </span>{' '}
                                    </div>
                                    <div className={css.workerCountDiv}>
                                      {' '}
                                      <span>
                                        <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                      </span>
                                      <span>
                                        {r?.endTime?.hours.value} : {r?.endTime?.min.value}
                                      </span>{' '}
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          ) : null}
                        </>
                      );
                    })}
                </div>
                {currentListing?.attributes?.description ? (
                  <div className={css.descContainer}>
                    <span>{currentListing?.attributes?.description}</span>
                  </div>
                ) : null}
                {currentListing?.attributes?.publicData?.workerYouNeed ? (
                  <p className={css.TotalWorkerDiv}>
                    <FormattedMessage id="SearchPage.totalProposedWorker" />{' '}
                    <b>{currentListing?.attributes?.publicData?.workerYouNeed}</b>
                  </p>
                ) : null}
                {currentListing?.attributes?.publicData?.listingAddress?.address ||
                currentListing?.attributes?.publicData?.listingAddress?.postalCode ? (
                  <div className={css.locationDiv}>
                    <div className={css.locateIcon}>
                      <Location className={css.location} />{' '}
                    </div>
                    <span>
                      {' '}
                      {currentListing?.attributes?.publicData?.listingAddress?.address},{' '}
                      {currentListing?.attributes?.publicData?.listingAddress?.postalCode}
                    </span>
                  </div>
                ) : null}
                {/* <hr className={css.hrColor} /> */}
                <div className={css.footerDiv}>
                  <div className={css.IdAndDate}>
                    <div className={css.IdDiv}>
                      <FormattedMessage id="SearchPage.gigId" />{' '}
                      <span>
                        {currentListing?.attributes?.publicData?.gigId
                          ? currentListing?.attributes?.publicData?.gigId
                          : 'G-123456'}
                      </span>
                    </div>
                    <span className={css.dash}>
                      {!!publishedDate && publishedDate !== undefined ? ` - ` : null}
                    </span>
                    {!!publishedDate && publishedDate !== undefined ? (
                      <div className={css.publicationDiv}>
                        <FormattedMessage id="SearchPage.Dateofpublication" />{' '}
                        <span>{moment(publishedDate).format('DD/MM/YY')} </span>
                      </div>
                    ) : null}
                  </div>

                  {/* <div>Download Invoice</div> */}
                </div>
              </div>
            </div>
            {/* <Overlay
              message={intl.formatMessage(
                { id: 'ManageListingCard.draftOverlayText' },
                { listingTitle: title }
              )}
            >
        
              <span className={css.editOverlayDraftSubMsg}>
                <FormattedMessage id="ManageListingCard.editOverlayDraftSubMsg" />
              </span>
              <NamedLink
                className={css.finishListingDraftLink}
                name="EditListingPage"
                params={{ id, slug, type: LISTING_PAGE_PARAM_TYPE_DRAFT, tab: 'ContactInfo' }}
              >
                <FormattedMessage id="ManageListingCard.finishListingDraft" />
              </NamedLink>
            </Overlay> */}
          </div>
        ) : null}
        {isClosed ? (
          <>
            <div className={classNames(css.listingCardwrapper)} key={currentListing?.id?.uuid}>
              <div className={css.heading}>
                <div className={css.headingName} onClick={seeListing}>
                  {' '}
                  {currentListing?.attributes?.title}
                </div>
                <span className={css.closeTag} onClick={seeListing}>
                  {isCancelledByBusiness
                    ? intl.formatMessage(
                        { id: 'ManagelistingCard.listingClose' },
                        { date: cancelDateStr }
                      )
                    : intl.formatMessage({ id: 'ManageListingCard.closedGig' })}
                </span>

                {isProvider ? (
                  <div className={css.editAndCloseDiv}>
                    {/* {hasWorkers ? null : (
                      <CreateGIgDisabledInfo
                        gigCreationDisabledChildren={
                          <InlineTextButton className={css.editDiv}>
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </InlineTextButton>
                        }
                        gigCreationEnabledChildren={
                          <NamedLink
                            className={css.editDiv}
                            name="EditListingPage"
                            params={{ id, slug, type: editListingLinkType, tab: 'description' }}
                          >
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </NamedLink>
                        }
                        popupId={'manageListingCard.edit'}
                      />
                    )} */}

                    {/* <button
                      // className={css.openListingButton}
                      className={css.openBtn}
                      disabled={!!actionsInProgressListingId}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!actionsInProgressListingId) {
                          onOpenListing(currentListing.id, params, search);
                        }
                      }}
                    >
                      <FormattedMessage id="ManageListingCard.openListing" />
                    </button> */}
                    {/* <button
                      className={css.cancelDiv}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!actionsInProgressListingId) {
                          onToggleMenu(null);
                          onCloseListing(currentListing);
                        }
                      }}
                    >
                      {' '}
                      <CloseIcon className={css.CrossIcon} />
                      <FormattedMessage id="EditListingCard.closejob" />
                    </button> */}
                    {/* <button
                      className={css.draftCopyButton}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      // data-tip
                      // data-for="copyTip"
                    >
                      <div className={css.draftCopyIcon}>
                        <CopyIcon />
                        <FormattedMessage id="ManageListingCard.copyButton" />
                      </div>
                      {isClosed && (
                        <>
                          {' '}
                          <span id={`${id}copyTip`} className={css.tooltipIcon}>
                            <InfoIcon />
                          </span>
                          <ReactTooltip
                            anchorId={`${id}copyTip`}
                            place="right"
                            effect="solid"
                            clickable={false}
                            className={css.categoryTooltip}
                            content={<FormattedMessage id="ManageListingCard.copyNotAllowedTip" />}
                          />
                        </>
                      )}
                    </button> */}
                    <CreateGIgDisabledInfo
                      gigCreationDisabledChildren={
                        <div className={`${css.copyButton} ${isDraft && css.disabled}`}>
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </div>
                      }
                      gigCreationEnabledChildren={
                        <button
                          className={`${css.copyButton} ${isDraft && css.disabled}`}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (isClosed) {
                              onCopyOwnListing(listing.id.uuid).then(response => {
                                const id = response.data.id.uuid;
                                history.push(
                                  createResourceLocatorString(
                                    'EditListingPage',
                                    routeConfiguration(),
                                    { id, slug, type: 'draft', tab: 'availability' },
                                    {}
                                  )
                                );
                              });
                            }
                          }}
                          // data-tip
                          // data-for="copyTip"
                        >
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </button>
                      }
                      popupId={'manageListingCard.copy'}
                    />

                    <div className={css.CardMenu}>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDotIconDiv(!dotIconDiv);
                        }}
                      >
                        <DotIcon />
                      </button>

                      <>
                        {dotIconDiv ? (
                          <div className={css.mobEditAndCloseDiv}>
                            {/* {hasWorkers ? null : (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <InlineTextButton className={css.MobeditDiv}>
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </InlineTextButton>
                                }
                                gigCreationEnabledChildren={
                                  <NamedLink
                                    className={css.MobeditDiv}
                                    name="EditListingPage"
                                    params={{
                                      id,
                                      slug,
                                      type: editListingLinkType,
                                      tab: 'description',
                                    }}
                                  >
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </NamedLink>
                                }
                                popupId={'manageListingCard.edit'}
                              />
                            )} */}

                            {shouldClose ? (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <div className={css.MobcancelDiv}>
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </div>
                                }
                                gigCreationEnabledChildren={
                                  <button
                                    className={css.MobcancelDiv}
                                    onClick={event => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (!actionsInProgressListingId) {
                                        onToggleMenu(null);
                                        if (typeof onCloseListing === 'function') {
                                          onCloseListing(currentListing);
                                        }
                                      }
                                    }}
                                  >
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </button>
                                }
                                popupId={'manageListingCard.close'}
                              />
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    </div>
                  </div>
                ) : null}
              </div>
              <div onClick={seeListing}>
                <div className={css.JobDetailsSubHeadingDiv}>
                  <span className={css.category}>
                    {jobCategoryOptions
                      .filter(f =>
                        currentListing?.attributes?.publicData.jobCategories.find(
                          m => m.category === f.value
                        )
                      )
                      .map(m => m.label[userNativeLang])}
                  </span>

                  <span className={css.subcategory}>
                    -{' '}
                    {
                      reducedJobSubCategory.find(
                        m =>
                          currentListing?.attributes?.publicData.jobCategories[0].subCategory ===
                          m.value
                      ).label[userNativeLang]
                    }
                  </span>
                </div>
                {formattedPrice ? (
                  <span className={css.PriceTag}>
                    <FormattedMessage id="SearchPage.Priceperhour" /> &nbsp;
                    <span>{formattedPrice}</span>
                  </span>
                ) : null}
                <div className={css.hourDiv}>
                  {scheduleType === 'oneTime' &&
                    currentListing?.attributes?.publicData.oneTimeScheduleList &&
                    currentListing?.attributes?.publicData?.oneTimeScheduleList.map((m, indx) => {
                      return (
                        <>
                          {indx > 0 ? (
                            <>
                              {/* <hr className={css.hrColor} /> */}
                              <div className={css.workPerDay}>
                                {durationHours ? (
                                  <FormattedMessage id="SearchPage.workPerDay" />
                                ) : null}
                                &nbsp;
                                <b>
                                  {durationHours ? (
                                    <>
                                      {durationHours}
                                      {` `}
                                      <FormattedMessage id="ListingCardCustom.hours" />
                                    </>
                                  ) : null}
                                  {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                    ? ` ${durationMinutes} minutes`
                                    : null}
                                </b>{' '}
                                {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                                  <>
                                    -&nbsp;
                                    <FormattedMessage id="SearchPage.unpaidBreak" />
                                    &nbsp;
                                    <b>{m?.unpaidBreak?.value} Minutes</b>
                                  </>
                                ) : null}
                                {m?.startDate?.date ? (
                                  <>
                                    {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                    <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(m?.startDate?.date)}</b>
                                  </>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <div className={css.workPerDay}>
                              {durationHours ? (
                                <FormattedMessage id="SearchPage.workPerDay" />
                              ) : null}
                              &nbsp;
                              <b>
                                {durationHours ? (
                                  <>
                                    {durationHours}
                                    {` `}
                                    <FormattedMessage id="ListingCardCustom.hours" />
                                  </>
                                ) : null}
                                {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                  ? ` ${durationMinutes} minutes`
                                  : null}
                              </b>
                              {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                                <>
                                  &nbsp;-&nbsp;
                                  <FormattedMessage id="SearchPage.unpaidBreak" />
                                  &nbsp;
                                  <b>{m?.unpaidBreak?.value} Minutes</b>
                                </>
                              ) : null}
                              {m?.startDate?.date ? (
                                <>
                                  {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                  <FormattedMessage id="SearchPage.Days" />{' '}
                                  <b> {formatDate(m?.startDate?.date)}</b>
                                </>
                              ) : null}
                            </div>
                          )}
                          <div className={css.scheduleDiv}>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                              </span>
                              <span>
                                {m?.startTime?.hours.value} : {m?.startTime?.min.value} &nbsp;-
                                &nbsp;
                              </span>{' '}
                            </div>
                            <div className={css.workerCountDiv}>
                              {' '}
                              <span>
                                <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                              </span>
                              <span>
                                {m?.endTime?.hours.value} : {m?.endTime?.min.value}
                              </span>{' '}
                            </div>
                          </div>
                        </>
                      );
                    })}

                  {scheduleType === 'recurring' &&
                    currentListing?.attributes?.publicData?.recurringScheduleList &&
                    currentListing?.attributes?.publicData?.recurringScheduleList.map((r, indx) => {
                      const recurringDays =
                        typeof r?.recurringDays === 'string'
                          ? r?.recurringDays?.split(',')
                          : r?.recurringDays?.map(m => m);
                      return (
                        <>
                          {r.startDate?.date !== null ? (
                            <>
                              {indx > 0 &&
                              currentListing?.attributes?.publicData?.recurringScheduleList[0]?.date
                                ?.date !== null ? (
                                <>
                                  {/* <hr className={css.hrColor} /> */}
                                  <div className={css.recWorkperDay}>
                                    {recdurationHours ? (
                                      <FormattedMessage id="SearchPage.workPerDay" />
                                    ) : null}
                                    &nbsp;
                                    <b>
                                      {' '}
                                      {recdurationHours ? (
                                        <>
                                          {recdurationHours}
                                          {` `}
                                          <FormattedMessage id="ListingCardCustom.hours" />
                                        </>
                                      ) : null}
                                      {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                        ? ` ${recdurationMinutes} minutes`
                                        : null}
                                    </b>{' '}
                                    {r?.unpaidBreak?.value &&
                                    parseInt(r?.unpaidBreak?.value) > 0 ? (
                                      <>
                                        -&nbsp;
                                        <FormattedMessage id="SearchPage.unpaidBreak" />
                                        &nbsp;
                                        <b>{r?.unpaidBreak?.value} Minutes</b>
                                      </>
                                    ) : null}
                                  </div>
                                  <div className={css.workPerDay}>
                                    <FormattedMessage id="SearchPage.recurringDays" />
                                    &nbsp;
                                    {recurringDays[0] === '' ? (
                                      <>
                                        <b>
                                          <FormattedMessage id="SearchPage.Daily" />
                                        </b>
                                      </>
                                    ) : (
                                      recurringDays.map((m, indx) =>
                                        indx > 0 ? (
                                          <>
                                            <b>, {m}</b>
                                          </>
                                        ) : (
                                          <b>{m}</b>
                                        )
                                      )
                                    )}
                                    &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                    <FormattedMessage id="SearchPage.to" />{' '}
                                    <b> {formatDate(r?.endDate?.date)}</b>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className={css.recWorkperDay}>
                                    {recdurationHours ? (
                                      <FormattedMessage id="SearchPage.workPerDay" />
                                    ) : null}
                                    &nbsp;
                                    <b>
                                      {' '}
                                      {recdurationHours ? (
                                        <>
                                          {recdurationHours}
                                          {` `}
                                          <FormattedMessage id="ListingCardCustom.hours" />
                                        </>
                                      ) : null}
                                      {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                        ? ` ${recdurationMinutes} minutes`
                                        : null}
                                    </b>{' '}
                                    {r?.unpaidBreak?.value &&
                                    parseInt(r?.unpaidBreak?.value) > 0 ? (
                                      <>
                                        -&nbsp;
                                        <FormattedMessage id="SearchPage.unpaidBreak" />
                                        &nbsp;
                                        <b>{r?.unpaidBreak?.value} Minutes</b>
                                      </>
                                    ) : null}
                                  </div>
                                  <div className={css.workPerDay}>
                                    <FormattedMessage id="SearchPage.recurringDays" />
                                    &nbsp;
                                    {recurringDays[0] === '' ? (
                                      <>
                                        <b>
                                          <FormattedMessage id="SearchPage.Daily" />
                                        </b>
                                      </>
                                    ) : (
                                      recurringDays.map((m, indx) =>
                                        indx > 0 ? (
                                          <>
                                            <b>, {m}</b>
                                          </>
                                        ) : (
                                          <b>{m}</b>
                                        )
                                      )
                                    )}
                                    &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                    <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                    <FormattedMessage id="SearchPage.to" />{' '}
                                    <b> {formatDate(r?.endDate?.date)}</b>
                                  </div>
                                  <div className={css.scheduleDiv}>
                                    <div className={css.workerCountDiv}>
                                      {' '}
                                      <span>
                                        <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                      </span>
                                      <span>
                                        {r?.startTime?.hours.value} : {r?.startTime?.min.value}{' '}
                                        &nbsp;- &nbsp;
                                      </span>{' '}
                                    </div>
                                    <div className={css.workerCountDiv}>
                                      {' '}
                                      <span>
                                        <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                      </span>
                                      <span>
                                        {r?.endTime?.hours.value} : {r?.endTime?.min.value}
                                      </span>{' '}
                                    </div>
                                  </div>
                                </>
                              )}
                            </>
                          ) : null}
                        </>
                      );
                    })}
                </div>
                {currentListing?.attributes?.description ? (
                  <div className={css.descContainer}>
                    <span>{currentListing?.attributes?.description}</span>
                  </div>
                ) : null}
                {currentListing?.attributes?.publicData?.workerYouNeed ? (
                  <p className={css.TotalWorkerDiv}>
                    <FormattedMessage id="SearchPage.totalProposedWorker" />{' '}
                    <b>{currentListing?.attributes?.publicData?.workerYouNeed}</b>
                  </p>
                ) : null}
                {currentListing?.attributes?.publicData?.listingAddress.address ||
                currentListing?.attributes?.publicData?.listingAddress.postalCode ? (
                  <div className={css.locationDiv}>
                    <div className={css.locateIcon}>
                      <Location className={css.location} />{' '}
                    </div>
                    <span>
                      {' '}
                      {currentListing?.attributes?.publicData?.listingAddress.address},{' '}
                      {currentListing?.attributes?.publicData?.listingAddress.postalCode}
                    </span>
                  </div>
                ) : null}

                {/* <hr className={css.hrColor} /> */}
                <div className={css.footerDiv}>
                  <div className={css.IdAndDate}>
                    <div className={css.IdDiv}>
                      <FormattedMessage id="SearchPage.gigId" />{' '}
                      <span>
                        {currentListing?.attributes?.publicData?.gigId
                          ? currentListing?.attributes?.publicData?.gigId
                          : 'G-123456'}
                      </span>
                    </div>
                    <span className={css.dash}>
                      {!!publishedDate && publishedDate !== undefined ? ` - ` : null}
                    </span>
                    {!!publishedDate && publishedDate !== undefined ? (
                      <div className={css.publicationDiv}>
                        <FormattedMessage id="SearchPage.Dateofpublication" />{' '}
                        <span>{moment(publishedDate).format('DD/MM/YY')} </span>
                      </div>
                    ) : null}
                  </div>
                  {shouldShowInvoice ? (
                    <div>
                      <span
                        onClick={e => {
                          e.stopPropagation();
                          handleOpenInvoiceModal({ e, id });
                        }}
                        className={css.viewInvoice}
                      >
                        <FormattedMessage id="ManageListingCard.viewInvoice" />
                      </span>
                      {/* <span onClick={(e)=>generateInvoice({e,id})}>
                    <Download />
                  </span> */}
                    </div>
                  ) : null}

                  {/* <div>Download Invoice</div> */}
                </div>
              </div>
            </div>
            {/* <Overlay
              message={intl.formatMessage({ id: 'ManageListingCard.closedListing' })}
              className={css.openOverlay}
            >
             
              <span className={css.editOverlayDraftSubMsg}>
                <FormattedMessage id="ManageListingCard.editOverlayClosedSubMsg" />
              </span>
              <button
                className={css.openListingButton}
                disabled={!!actionsInProgressListingId}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!actionsInProgressListingId) {
                    onOpenListing(currentListing.id, params, search);
                  }
                }}
              >
                <FormattedMessage id="ManageListingCard.openListing" />
              </button>
            </Overlay> */}
          </>
        ) : null}

        {isPendingApproval ? (
          <Overlay
            message={intl.formatMessage(
              { id: 'ManageListingCard.pendingApproval' },
              { listingTitle: title }
            )}
          />
        ) : null}
        {thisListingInProgress ? (
          <Overlay>
            <IconSpinner />
          </Overlay>
        ) : hasError ? (
          <Overlay errorMessage={intl.formatMessage({ id: 'ManageListingCard.actionFailed' })} />
        ) : null}
      </div>

      <div className={css.info}>
        {/* <div className={css.price}>
          {formattedPrice ? (
            <React.Fragment>
              <div className={css.priceValue} title={priceTitle}>
                {formattedPrice}
              </div>
              <div className={css.perUnit}>
                <FormattedMessage id={unitTranslationKey} />
              </div>
            </React.Fragment>
          ) : (
            <div className={css.noPrice}>
              <FormattedMessage id="ManageListingCard.priceNotSet" />
            </div>
          )}
        </div> */}

        {/* <div className={css.mainInfo}>
          <div className={css.titleWrapper}>
            <InlineTextButton
              rootClassName={titleClasses}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                history.push(createListingURL(routeConfiguration(), listing));
              }}
            >
              {formatTitle(title, MAX_LENGTH_FOR_WORDS_IN_TITLE)}
            </InlineTextButton>
          </div>
        </div> */}

        {/* <div className={css.manageLinks}> */}
        {/* <NamedLink
            className={css.manageLink}
            name="EditListingPage"
            params={{ id, slug, type: editListingLinkType, tab: 'description' }}
          >
            <FormattedMessage id="ManageListingCard.editListing" />
          </NamedLink> */}

        {/* {availabilityEnabled ? ( */}
        {/* <React.Fragment> */}
        {/* <span className={css.manageLinksSeparator}>{' • '}</span> */}

        {/* <NamedLink
                className={css.manageLink}
                name="EditListingPage"
                params={{ id, slug, type: editListingLinkType, tab: 'availability' }}
              >
                <FormattedMessage id="ManageListingCard.manageAvailability" />
              </NamedLink> */}
        {/* </React.Fragment> */}
        {/* // ) : null} */}
        {/* </div> */}

        <NamedLink
          name="ListingPage"
          params={{
            id: currentListing?.id?.uuid,
            slug: createSlug(currentListing?.attributes?.title),
          }}
        >
          {!isDraft && !isClosed ? (
            <div
              className={classNames(css.listingCardwrapper, { [css.draftNoImage]: isDraft })}
              key={currentListing?.id?.uuid}
            >
              <div className={css.heading}>
                <div className={css.headingName}> {currentListing?.attributes?.title}</div>
                {isProvider ? (
                  <div className={css.editAndCloseDiv}>
                    {hasWorkers ? null : (
                      <CreateGIgDisabledInfo
                        gigCreationDisabledChildren={
                          <InlineTextButton className={css.editDiv}>
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </InlineTextButton>
                        }
                        gigCreationEnabledChildren={
                          <NamedLink
                            className={css.editDiv}
                            name="EditListingPage"
                            params={{ id, slug, type: editListingLinkType, tab: 'description' }}
                          >
                            <EditIcon className={css.editPenIcon} />
                            <FormattedMessage id="EditListingCard.editjob" />
                          </NamedLink>
                        }
                        popupId={'manageListingCard.edit'}
                      />
                    )}
                    {shouldClose ? (
                      <CreateGIgDisabledInfo
                        gigCreationDisabledChildren={
                          <div className={css.cancelDiv}>
                            {' '}
                            <CloseIcon className={css.CrossIcon} />
                            <FormattedMessage id="EditListingCard.closejob" />
                          </div>
                        }
                        gigCreationEnabledChildren={
                          <button
                            className={css.cancelDiv}
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              if (!actionsInProgressListingId) {
                                onToggleMenu(null);
                                if (typeof onCloseListing === 'function') {
                                  onCloseListing(currentListing);
                                }
                              }
                            }}
                          >
                            <CloseIcon className={css.CrossIcon} />
                            <FormattedMessage id="EditListingCard.closejob" />
                          </button>
                        }
                        popupId={'manageListingCard.close'}
                      />
                    ) : null}
                    <CreateGIgDisabledInfo
                      gigCreationDisabledChildren={
                        <div className={`${css.copyButton} ${isDraft && css.disabled}`}>
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </div>
                      }
                      gigCreationEnabledChildren={
                        <button
                          className={`${css.copyButton} ${isDraft && css.disabled}`}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (!isDraft) {
                              onCopyOwnListing(listing.id.uuid).then(response => {
                                const id = response.data.id.uuid;
                                history.push(
                                  createResourceLocatorString(
                                    'EditListingPage',
                                    routeConfiguration(),
                                    { id, slug, type: 'draft', tab: 'availability' },
                                    {}
                                  )
                                );
                              });
                            }
                          }}
                          // data-tip
                          // data-for="copyTip"
                        >
                          <div className={`${css.iconContainer} `}>
                            <CopyIcon />
                            <FormattedMessage id="ManageListingCard.copyButton" />
                          </div>
                        </button>
                      }
                      popupId={'manageListingCard.copy'}
                    />

                    <div className={css.CardMenu}>
                      <button
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDotIconDiv(!dotIconDiv);
                        }}
                      >
                        <DotIcon />
                      </button>

                      <>
                        {dotIconDiv ? (
                          <div className={css.mobEditAndCloseDiv}>
                            {hasWorkers ? null : (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <InlineTextButton className={css.MobeditDiv}>
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </InlineTextButton>
                                }
                                gigCreationEnabledChildren={
                                  <NamedLink
                                    className={css.MobeditDiv}
                                    name="EditListingPage"
                                    params={{
                                      id,
                                      slug,
                                      type: editListingLinkType,
                                      tab: 'description',
                                    }}
                                  >
                                    <EditIcon className={css.editPenIcon} />
                                    <FormattedMessage id="EditListingCard.editjob" />
                                  </NamedLink>
                                }
                                popupId={'manageListingCard.edit'}
                              />
                            )}

                            {shouldClose ? (
                              <CreateGIgDisabledInfo
                                gigCreationDisabledChildren={
                                  <div className={css.MobcancelDiv}>
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </div>
                                }
                                gigCreationEnabledChildren={
                                  <button
                                    className={css.MobcancelDiv}
                                    onClick={event => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      if (!actionsInProgressListingId) {
                                        onToggleMenu(null);
                                        if (typeof onCloseListing === 'function') {
                                          onCloseListing(currentListing);
                                        }
                                      }
                                    }}
                                  >
                                    {' '}
                                    <CloseIcon className={css.CrossIcon} />
                                    <FormattedMessage id="EditListingCard.closejob" />
                                  </button>
                                }
                                popupId={'manageListingCard.close'}
                              />
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className={css.JobDetailsSubHeadingDiv}>
                <span className={css.category}>
                  {jobCategoryOptions
                    .filter(f =>
                      currentListing?.attributes?.publicData.jobCategories.find(
                        m => m.category === f.value
                      )
                    )
                    .map(m => m.label[userNativeLang])}
                </span>

                <span className={css.subcategory}>
                  -{' '}
                  {
                    reducedJobSubCategory.find(
                      m =>
                        currentListing?.attributes?.publicData.jobCategories[0].subCategory ===
                        m.value
                    ).label[userNativeLang]
                  }
                </span>
              </div>
              {formattedPrice ? (
                <span className={css.PriceTag}>
                  <FormattedMessage id="SearchPage.Priceperhour" /> &nbsp;
                  <span>{formattedPrice}</span>
                </span>
              ) : null}
              <div className={css.hourDiv}>
                {scheduleType === 'oneTime' &&
                  currentListing?.attributes?.publicData.oneTimeScheduleList &&
                  currentListing?.attributes?.publicData?.oneTimeScheduleList.map((m, indx) => {
                    return (
                      <>
                        {indx > 0 ? (
                          <>
                            {/* <hr className={css.hrColor} /> */}
                            <div className={css.workPerDay}>
                              {durationHours ? (
                                <FormattedMessage id="SearchPage.workPerDay" />
                              ) : null}
                              &nbsp;
                              <b>
                                {durationHours ? (
                                  <>
                                    {durationHours}
                                    {` `}
                                    <FormattedMessage id="ListingCardCustom.hours" />
                                  </>
                                ) : null}
                                {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                  ? ` ${durationMinutes} minutes`
                                  : null}
                              </b>{' '}
                              {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                                <>
                                  -&nbsp;
                                  <FormattedMessage id="SearchPage.unpaidBreak" />
                                  &nbsp;
                                  <b>{m?.unpaidBreak?.value} Minutes</b>
                                </>
                              ) : null}
                              {m?.startDate?.date ? (
                                <>
                                  {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                  <FormattedMessage id="SearchPage.Days" />{' '}
                                  <b> {formatDate(m?.startDate?.date)}</b>
                                </>
                              ) : null}
                            </div>
                            <div className={css.scheduleDiv}>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                </span>
                                <span>
                                  {m?.startTime?.hours.value} : {m?.startTime?.min.value} &nbsp;-
                                  &nbsp;
                                </span>{' '}
                              </div>
                              <div className={css.workerCountDiv}>
                                {' '}
                                <span>
                                  <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                </span>
                                <span>
                                  {m?.endTime?.hours.value} : {m?.endTime?.min.value}
                                </span>{' '}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className={css.workPerDay}>
                            {durationHours ? <FormattedMessage id="SearchPage.workPerDay" /> : null}
                            &nbsp;
                            <b>
                              {durationHours ? (
                                <>
                                  {durationHours}
                                  {` `}
                                  <FormattedMessage id="ListingCardCustom.hours" />
                                </>
                              ) : null}
                              {durationMinutes !== 0 && typeof durationMinutes !== NaN
                                ? ` ${durationMinutes} minutes`
                                : null}
                            </b>
                            {m?.unpaidBreak?.value && parseInt(m?.unpaidBreak?.value) > 0 ? (
                              <>
                                &nbsp;-&nbsp;
                                <FormattedMessage id="SearchPage.unpaidBreak" />
                                &nbsp;
                                <b>{m?.unpaidBreak?.value} Minutes</b>
                              </>
                            ) : null}
                            {m?.startDate?.date ? (
                              <>
                                {Object.values(m?.startDate?.date).length === 0 ? null : ` - `}
                                <FormattedMessage id="SearchPage.Days" />{' '}
                                <b> {formatDate(m?.startDate?.date)}</b>
                              </>
                            ) : null}
                          </div>
                        )}
                        <div className={css.scheduleDiv}>
                          <div className={css.workerCountDiv}>
                            {' '}
                            <span>
                              <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                            </span>
                            <span>
                              {m?.startTime?.hours.value} : {m?.startTime?.min.value} &nbsp;- &nbsp;
                            </span>{' '}
                          </div>
                          <div className={css.workerCountDiv}>
                            {' '}
                            <span>
                              <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                            </span>
                            <span>
                              {m?.endTime?.hours.value} : {m?.endTime?.min.value}
                            </span>{' '}
                          </div>
                        </div>
                      </>
                    );
                  })}

                {scheduleType === 'recurring' &&
                  currentListing?.attributes?.publicData?.recurringScheduleList &&
                  currentListing?.attributes?.publicData?.recurringScheduleList?.map((r, indx) => {
                    const recurringDays =
                      typeof r?.recurringDays === 'string'
                        ? r?.recurringDays?.split(',')
                        : r?.recurringDays?.map(m => m);
                    return (
                      <>
                        {r?.startDate?.date !== null ? (
                          <>
                            {indx > 0 &&
                            currentListing?.attributes?.publicData?.recurringScheduleList[0]?.date
                              ?.date !== null ? (
                              <>
                                {/* <hr className={css.hrColor} /> */}
                                <div className={css.recWorkperDay}>
                                  {recdurationHours ? (
                                    <FormattedMessage id="SearchPage.workPerDay" />
                                  ) : null}
                                  &nbsp;
                                  <b>
                                    {' '}
                                    {recdurationHours ? (
                                      <>
                                        {recdurationHours}
                                        {` `}
                                        <FormattedMessage id="ListingCardCustom.hours" />
                                      </>
                                    ) : null}
                                    {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                      ? ` ${recdurationMinutes} minutes`
                                      : null}
                                  </b>{' '}
                                  {r?.unpaidBreak?.value && parseInt(r?.unpaidBreak?.value) > 0 ? (
                                    <>
                                      -&nbsp;
                                      <FormattedMessage id="SearchPage.unpaidBreak" />
                                      &nbsp;
                                      <b>{r?.unpaidBreak?.value} Minutes</b>
                                    </>
                                  ) : null}
                                </div>
                                <div className={css.workPerDay}>
                                  <FormattedMessage id="SearchPage.recurringDays" />
                                  &nbsp;
                                  {recurringDays[0] === '' ? (
                                    <>
                                      <b>
                                        <FormattedMessage id="SearchPage.Daily" />
                                      </b>
                                    </>
                                  ) : (
                                    recurringDays.map((m, indx) =>
                                      indx > 0 ? (
                                        <>
                                          <b>, {m}</b>
                                        </>
                                      ) : (
                                        <b>{m}</b>
                                      )
                                    )
                                  )}
                                  &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                  <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                  <FormattedMessage id="SearchPage.to" />{' '}
                                  <b> {formatDate(r?.endDate?.date)}</b>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className={css.recWorkperDay}>
                                  {recdurationHours ? (
                                    <FormattedMessage id="SearchPage.workPerDay" />
                                  ) : null}
                                  &nbsp;
                                  <b>
                                    {' '}
                                    {recdurationHours ? (
                                      <>
                                        {recdurationHours}
                                        {` `}
                                        <FormattedMessage id="ListingCardCustom.hours" />
                                      </>
                                    ) : null}
                                    {recdurationMinutes !== 0 && typeof recdurationMinutes !== NaN
                                      ? ` ${recdurationMinutes} minutes`
                                      : null}
                                  </b>{' '}
                                  {r?.unpaidBreak?.value && parseInt(r?.unpaidBreak?.value) > 0 ? (
                                    <>
                                      -&nbsp;
                                      <FormattedMessage id="SearchPage.unpaidBreak" />
                                      &nbsp;
                                      <b>{r?.unpaidBreak?.value} Minutes</b>
                                    </>
                                  ) : null}
                                </div>
                                <div className={css.workPerDay}>
                                  <FormattedMessage id="SearchPage.recurringDays" />
                                  &nbsp;
                                  {recurringDays[0] === '' ? (
                                    <>
                                      <b>
                                        <FormattedMessage id="SearchPage.Daily" />
                                      </b>
                                    </>
                                  ) : (
                                    recurringDays.map((m, indx) =>
                                      indx > 0 ? (
                                        <>
                                          <b>, {m}</b>
                                        </>
                                      ) : (
                                        <b>{m}</b>
                                      )
                                    )
                                  )}
                                  &nbsp;- <FormattedMessage id="SearchPage.Days" />{' '}
                                  <b> {formatDate(r?.startDate?.date)}</b>{' '}
                                  <FormattedMessage id="SearchPage.to" />{' '}
                                  <b> {formatDate(r?.endDate?.date)}</b>
                                </div>
                                <div className={css.scheduleDiv}>
                                  <div className={css.workerCountDiv}>
                                    {' '}
                                    <span>
                                      <FormattedMessage id="FieldDateTimeInput.startTime" /> :
                                    </span>
                                    <span>
                                      {r?.startTime?.hours.value} : {r?.startTime?.min.value}{' '}
                                      &nbsp;- &nbsp;
                                    </span>{' '}
                                  </div>
                                  <div className={css.workerCountDiv}>
                                    {' '}
                                    <span>
                                      <FormattedMessage id="FieldDateTimeInput.endTime" /> :
                                    </span>
                                    <span>
                                      {r?.endTime?.hours.value} : {r?.endTime?.min.value}
                                    </span>{' '}
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        ) : null}
                      </>
                    );
                  })}
              </div>
              {currentListing?.attributes?.description ? (
                <div className={css.descContainer}>
                  {/* <span>{currentListing?.attributes?.description}</span> */}
                  <ReadMoreButton description={currentListing?.attributes?.description} />
                </div>
              ) : null}
              {currentListing?.attributes?.publicData?.workerYouNeed ? (
                <p className={css.TotalWorkerDiv}>
                  <FormattedMessage id="SearchPage.totalProposedWorker" />{' '}
                  <b>{currentListing?.attributes?.publicData?.workerYouNeed}</b>
                </p>
              ) : null}
              {currentListing?.attributes?.publicData?.listingAddress.address ||
              currentListing?.attributes?.publicData?.listingAddress.postalCode ? (
                <div className={css.locationDiv}>
                  <div className={css.locateIcon}>
                    <Location className={css.location} />{' '}
                  </div>
                  <span>
                    {' '}
                    {currentListing?.attributes?.publicData?.listingAddress.address},{' '}
                    {currentListing?.attributes?.publicData?.listingAddress.postalCode}
                  </span>
                </div>
              ) : null}
              {/* <hr className={css.hrColor} /> */}
              <div className={css.footerDiv}>
                <div className={css.IdAndDate}>
                  <div className={css.IdDiv}>
                    <FormattedMessage id="SearchPage.gigId" />{' '}
                    <span>
                      {currentListing?.attributes?.publicData?.gigId
                        ? currentListing?.attributes?.publicData?.gigId
                        : 'G-123456'}
                    </span>
                  </div>
                  <span className={css.dash}>
                    {!!publishedDate && publishedDate !== undefined ? ` - ` : null}
                  </span>
                  {!!publishedDate && publishedDate !== undefined ? (
                    <div className={css.publicationDiv}>
                      <FormattedMessage id="SearchPage.Dateofpublication" />{' '}
                      <span>{moment(publishedDate).format('DD/MM/YY')} </span>
                    </div>
                  ) : null}
                </div>
                {/* {currentListing?.attributes?.metadata?.acceptedWorkers?.length ? (
                  <div>
                    <span
                      onClick={e => handleOpenInvoiceModal({ e, id })}
                      className={css.viewInvoice}
                    >
                      <FormattedMessage id="ManageListingCard.viewInvoice" />
                    </span>
                    
                  </div>
                ) : null} */}
              </div>
            </div>
          ) : null}
        </NamedLink>
      </div>

      <Modal
        id="BookingPanel.NoCategoryModal"
        isOpen={!!showModal}
        onClose={() => setShowModal(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        className={css.BlockModal}
        // showCloseButton={false}
        // containerClassName={classNames({ [css.invoiceModal]: isProfileVerified === false })}
      >
        {shouldShowInvoice ? (
          <FinalForm
            {...props}
            mutators={{ ...arrayMutators }}
            onSubmit={() => {}}
            render={fieldRenderProps => {
              const {
                className,
                currentUser,
                handleSubmit,
                intl,
                invalid,
                onImageUpload,
                pristine,
                inProgress,
                profileImage,
                rootClassName,
                updateInProgress,
                updateProfileError,
                uploadImageError,
                uploadInProgress,
                form,
                formId,
                values,
                onUpdateProfile,
                initialValues,
                userNativeLang,
                history,
                jobCategories,
                isRequired,
                onSearchListings,
                updated,
                // pristine,
                ready,
              } = fieldRenderProps;

              return (
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                  }}
                  className={css.modalComp}
                >
                  <div className={css.InvoiceContainer} id={id}>
                    <Invoice
                      currentListing={currentListing}
                      userNativeLang={userNativeLang}
                      user={user}
                      jobCategoryOptions={jobCategoryOptions}
                      jobSubCategoryOptions={jobSubCategoryOptions}
                      reducedJobSubCategory={reducedJobSubCategory}
                      intl={intl}
                      invoiceData={invoiceData}
                      // FormattedMessage={FormattedMessage}
                    />
                  </div>
                  <div className={css.btnDiv}>
                    <Button
                      onClick={e =>
                        generateInvoice({
                          e,
                          id,
                          name: currentListing?.attributes?.title,
                          status: invoiceData?.[0]?.paymentIntentStatus,
                        })
                      }
                      className={css.submitButton}
                      inProgress={downloading}
                    >
                      <FormattedMessage id="ListingPublishedPage.invoiceBtn" />
                    </Button>
                  </div>
                </Form>
              );
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

ManageListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  actionsInProgressListingId: null,
  renderSizes: null,
  availabilityEnabled: config.enableAvailability,
};

const { bool, func, shape, string } = PropTypes;

ManageListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  hasClosingError: bool.isRequired,
  hasOpeningError: bool.isRequired,
  intl: intlShape.isRequired,
  listing: propTypes.ownListing.isRequired,
  isMenuOpen: bool.isRequired,
  actionsInProgressListingId: shape({ uuid: string.isRequired }),
  onCloseListing: func.isRequired,
  onOpenListing: func.isRequired,
  onToggleMenu: func.isRequired,
  availabilityEnabled: bool,

  // Responsive image sizes hint
  renderSizes: string,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(ManageListingCardComponent);
